// src/encounter/risk/lib/pain-fields.ts
// The 10 Pain Assessment fields, in the exact grid order and with the exact (including
// misspelled) option strings the Flutter app uses. Two fields share the label "Location" —
// they are DIFFERENT fields (free-text address vs a multi-select body-relation chip set) and
// are kept distinct by `id`, not by label.
export type PainFieldType = 'onset' | 'text' | 'chip-single' | 'chip-multi' | 'textarea';

export interface PainFieldDef {
  readonly id: string;
  readonly label: string;
  readonly type: PainFieldType;
  readonly options?: readonly string[];
  readonly placeholder?: string;
  readonly maxLength?: number;
  /** When set, this field's value lives on a NAMED `PainAssessmentEntry` property (per the
   *  spec's persistence field-name list) rather than under `items[id]`. */
  readonly storesTo?: 'onset' | 'address' | 'pharmacologicalInterventions';
}

export const PAIN_FIELDS: readonly PainFieldDef[] = [
  {
    id: 'onset',
    label: 'Onset',
    type: 'onset',
    storesTo: 'onset',
  },
  {
    id: 'location-address',
    label: 'Location',
    type: 'text',
    placeholder: 'Enter location',
    storesTo: 'address',
  },
  {
    id: 'pain-character',
    label: 'Pain Character',
    type: 'chip-single',
    options: ['Acute', 'Burning', 'Aching', 'Chronic', 'Constant', 'Crushing'],
  },
  {
    id: 'timing',
    label: 'Timing',
    type: 'chip-multi',
    // "Continious" is misspelled in the Flutter source (pain_assessment fields) — reproduced
    // verbatim here, deliberately, for faithful parity. Do not "fix" the spelling.
    options: ['Abrupt', 'After meal', 'Awakened from sleep', 'Continious', 'Primarily nocturnal'],
  },
  {
    id: 'location-region',
    label: 'Location',
    type: 'chip-multi',
    // Medial/Lateral exist in the Flutter source but are commented out there — omitted here too.
    options: ['Ipsilateral', 'Contralateral', 'Proximal', 'Superior', 'Inferior'],
  },
  {
    id: 'relieving-factors',
    label: 'Relieving Factors',
    type: 'chip-multi',
    // "Insiration" is misspelled in the Flutter source — reproduced verbatim, deliberately.
    options: [
      'Abated on their own',
      'Abated when on medications',
      'Partially resolved on their own',
      'Increased with Insiration',
      'Decrease with movement',
      'Exacerbate with movement',
    ],
  },
  {
    id: 'associated-signs-and-symptoms',
    label: 'Associated Signs and Symptoms',
    type: 'chip-single',
    options: ['Numbness', 'Tingling', 'Weakness', 'Headache', 'Change in vision', 'Dizziness', 'Sexual dysfunction'],
  },
  {
    id: 'pharmacological-interventions',
    label: 'Non- and pharmacological interventions',
    type: 'textarea',
    maxLength: 200,
    placeholder: 'Enter comments',
    storesTo: 'pharmacologicalInterventions',
  },
  {
    id: 'psychological-status',
    label: 'Psychological Status',
    type: 'chip-single',
    options: ['Mood stable', 'Depressed', 'Feeling calm', 'Agitated', 'Relaxed', 'Fear'],
  },
  {
    id: 'conscious',
    label: 'Conscious',
    type: 'chip-single',
    options: ['Yes', 'No'],
  },
] as const;

/** Onset unit dropdown options — Flutter's onChange for this control is a no-op (see
 *  `pain-field-row.tsx`); the selection is decorative and never persisted. */
export const ONSET_UNIT_OPTIONS = ['days', 'weeks', 'mos'] as const;
